import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const ai = {
    headers: {
        index: 'AI Assistant',
        health: 'Financial Health',
        healthHistory: 'Score history',
        healthDetail: 'Analysis detail',
        tips: 'Action Plan',
        chat: 'Financial Chat',
        summary: 'Monthly Summary',
    },
    hub: {
        apiKey: {
            title: 'Optional API key',
            badge: 'Not configured',
            description: 'The app keeps working locally. Set it up only if you want AI answers and analyses.',
        },
        insightsTitle: 'Monthly insights',
        features: {
            chatTitle: 'Financial Chat',
            chatSubtitle: 'Ask about your spending, goals, and budgets',
            summaryTitle: 'Monthly Summary',
            summarySubtitle: 'Narrative report with the period highlights',
            tipsTitle: 'Action Plan',
            tipsSubtitle: '3 to 5 practical actions for your month',
        },
        health: {
            title: 'Financial Health',
            analysesSaved_one: '{{count}} saved analysis',
            analysesSaved_other: '{{count}} saved analyses',
            emptySubtitle: 'Score from 0 to 100 with analysis',
            deltaThisMonth: '{{delta}} this month',
            emptyDescription: 'Generate your first score and track how your finances evolve month by month.',
        },
    },
    chat: {
        suggestions: {
            sideBusiness: 'What\'s my profit from {{name}}?',
            receivables: 'Who owes me?',
            financings: 'How much is left on my financings?',
            loans: 'How much is left on my loans?',
            financingsAndLoans: 'How much is left on my financings and loans?',
            whereSpent: 'Where did I spend the most this month?',
            vsLastMonth: 'Did I spend more than last month?',
            canSpend: 'How much can I still spend?',
            budgets: 'Did any budget go over?',
            profit: 'What\'s my profit from…',
            savings: 'How are my savings?',
            emergencyFund: 'How is my emergency fund?',
        },
        homePlaceholder: 'Ask Kontto about your money…',
        profitPrefill: 'What\'s my profit from ',
        emptyPrompt: 'Ask anything about your finances',
        inputPlaceholder: 'Ask about your finances...',
        localAnswer: 'Local answer — {{warning}}',
        noKeyHint: 'Without AI set up, I answer the basics. I can answer these right away:',
        noKeySetup: 'Set up AI',
        errorFallback: 'I couldn\'t process your question.',
        periods: {
            this_month: 'this month',
            last_month: 'last month',
            last_3_months: 'in the last 3 months',
            last_6_months: 'in the last 6 months',
            last_12_months: 'in the last 12 months',
            this_year: 'this year',
        },
        cards: {
            seeTransactions: 'See entries',
            ranking: {
                title: 'Where you spent the most',
                answer: 'You have spent {{total}} {{period}}. The biggest share went to {{top}} ({{pct}}).',
                confirmedOnly: 'Confirmed entries only. Pending and scheduled ones are left out.',
                more_one: '+ {{count}} category',
                more_other: '+ {{count}} categories',
            },
            comparison: {
                title: 'Through day {{day}}, vs {{previousMonth}}',
                thisMonth: 'This month',
                answerMore: 'Through day {{day}} you spent {{current}}, {{diff}} more ({{pct}}) than the same period of {{previousMonth}}.',
                answerLess: 'Through day {{day}} you spent {{current}}, {{diff}} less ({{pct}}) than the same period of {{previousMonth}}.',
                answerSame: 'Through day {{day}} you spent {{current}}, about the same as the same period of {{previousMonth}}.',
                answerNoPrevious: 'Through day {{day}} you spent {{current}}. There is no spending in the same period of {{previousMonth}} to compare.',
                whatChanged: 'What changed the most',
                confirmedOnly: 'Confirmed entries only, in both months.',
            },
            available: {
                title: 'Until the end of the month',
                now: 'Available now',
                incoming: 'Still coming in',
                outgoing: 'Still going out',
                overdueIn: 'Overdue, to receive',
                overdueOut: 'Overdue, to pay',
                projectedEnd: 'Should be left',
                projectedShort: 'Should be short',
                perDay: '≈ {{amount}} per day for the next {{days}} days',
                reservedNote: '{{amount}} saved in pockets is not counted',
                answer: 'You have {{available}} available. Counting what still comes in and goes out until the end of the month, including what is overdue, {{projectedEnd}} should be left.',
                answerNegative: 'You have {{available}} available, but with what still goes out until the end of the month, including what is overdue, you should be {{projectedEnd}} short.',
            },
            budgets: {
                title: 'This month\'s budgets',
                of: '{{spent}} of {{limit}}',
                answerOver_one: '{{count}} budget went over the limit this month.',
                answerOver_other: '{{count}} budgets went over the limit this month.',
                answerWithin: 'All your budgets are within the limit this month.',
            },
            receivables: {
                title: 'Who owes me',
                total: 'Total to receive',
                when: 'Whenever possible',
                dueOn: 'Agreed for {{date}}',
                overdueOn: 'Overdue since {{date}}',
                more_one: '+ {{count}} person',
                more_other: '+ {{count}} people',
                answer_one: '{{count}} person owes you {{total}}.',
                answer_other: '{{count}} people owe you {{total}} in total.',
                answerOverdue_one: ' {{count}} is past the agreed date.',
                answerOverdue_other: ' {{count}} are past the agreed date.',
            },
            financings: {
                title: 'Financings',
                titleLoan: 'Loans',
                titleBoth: 'Financings and loans',
                kindFinancing: 'Financing',
                kindLoan: 'Loan',
                noun: {
                    financing_one: 'financing',
                    financing_other: 'financings',
                    loan_one: 'loan',
                    loan_other: 'loans',
                    both: 'financings and loans',
                },
                monthly: 'Monthly installments',
                balance: 'Outstanding balance',
                progress: '{{paid}} of {{total}} installments paid',
                next: 'Next on {{date}}',
                answer_one: 'You have 1 active {{noun}}, paying {{monthly}} a month. {{remaining}} installments left.',
                answer_other: 'You have {{count}} active {{noun}}, adding up to {{monthly}} a month in installments.',
            },
            overview: {
                title: '{{month}} summary',
                income: 'Income',
                expense: 'Expenses',
                result: 'Month result',
                available: 'Available now',
                reservedNote: '{{amount}} saved in pockets is left out',
                savings: 'Savings',
                health: 'Financial health',
                answer: 'Here is your {{month}} summary. You have {{available}} available, not counting what is saved in pockets.',
            },
            net: {
                titleProfit: 'Profit · {{label}}',
                titleLoss: 'Loss · {{label}}',
                income: 'Income',
                expense: 'Expenses',
                monthly: 'Month by month',
                answerProfit: 'From {{label}}, {{period}}, you made a profit of {{amount}}.',
                answerLoss: 'From {{label}}, {{period}}, you had a loss of {{amount}}.',
            },
            empty: {
                top_categories: 'No spending recorded in this period yet.',
                month_comparison: 'No spending this month or last month to compare yet.',
                available: 'I couldn\'t calculate the available balance right now.',
                budgets: 'You don\'t have budgets yet. Create one in Budgets to track it here.',
                net: 'I found no entries in those categories or tags for the period.',
                receivables: 'Nobody owes you anything right now. Loans to people live in Who owes me.',
                financings: 'You have no active financings or loans.',
            },
        },
    },
    summary: {
        emptyPrompt: 'Generate a narrative summary of your month with your real financial data',
        generateButton: 'Generate monthly summary',
        regenerateButton: 'Refresh summary',
        generatingButton: 'Generating summary...',
        sourceLocal: 'Local summary',
        sourceAi: 'AI-generated summary',
        highlightsTitle: 'Monthly highlights',
        copyPromptButton: 'Copy sent prompt',
        errorFallback: 'Error generating summary',
    },
    tips: {
        emptyPrompt: 'Pull down to build an action plan based on your financial situation',
        loadingLabel: 'Building action plan...',
        sourceLocal: 'Local action plan',
        sourceAi: 'AI-generated action plan',
        copyPromptButton: 'Copy sent prompt',
        errorFallback: 'Error generating tips',
    },
    copy: {
        copied: 'Prompt copied to clipboard.',
        shared: 'Copy unavailable in this build. Opened the system share sheet.',
        failed: 'Couldn\'t copy or share the prompt.',
    },
    consent: {
        title: 'AI analysis',
        subtitle: 'To generate answers, Kontto sends a summary of your finances directly to {{provider}}, under that provider\'s terms. This only happens with your authorization.',
        sharedTitle: 'What is sent',
        notSharedTitle: 'What never leaves your device',
        shared: {
            totals: 'Income and expense totals for the month and by category',
            indicators: 'Aggregate indicators (savings rate, card usage, emergency fund)',
            goalsBudgets: 'Active goals and budgets (name, target, and progress)',
            categoryNames: 'The names of your categories and tags, without amounts (in the chat, to understand questions like "profit from my app")',
        },
        notShared: {
            descriptions: 'Transaction descriptions and merchant names — unless you ask AI to categorize a statement, and the app asks first',
            accounts: 'Account names and card numbers',
            apiKey: 'Your API key (stays on your device)',
        },
        policyLink: 'Read the privacy policy',
        footnote: 'You can revoke this authorization anytime in Settings › AI Assistant.',
        decline: 'Not now',
        accept: 'Agree and continue',
    },
    health: {
        kicker: 'Financial health',
        deltaSuffix: 'this month',
        evolutionHint: 'The trend chart appears from the second analysis onward.',
        sourceLocal: 'Local analysis',
        sourceAi: 'AI analysis',
        trackMonthly: 'Track your progress month by month',
        copyPromptButton: 'Copy sent prompt',
        analyzingButton: 'Analyzing...',
        refreshButton: 'Refresh analysis',
        analyzeButton: 'Analyze financial health',
        analyzeError: 'Error analyzing',
        emptyTitle: 'Your financial health',
        emptyDescription:
            'Generate your first analysis to get a score from 0 to 100, see the factors that weigh the most, and start tracking your progress month by month.',
        range3m: '3M',
        range6m: '6M',
        range12m: '12M',
        rangeAll: 'All',
        historyEmpty:
            'No analysis saved yet. Generate one on the Financial Health screen to start building your history.',
        statCurrent: 'Current',
        statBest: 'Best',
        statAverage: 'Average',
        curveHint: 'Run at least two analyses to see the trend curve.',
        allAnalyses: 'All analyses',
        analyzedOn: 'Analyzed on {{date}}',
        summaryCopied: 'Summary copied to clipboard.',
        summaryCopyFailed: 'Couldn\'t copy the summary.',
        notFound: 'Analysis not found.',
        analyzedOnWithSource: 'Analyzed on {{date}} · {{source}}',
        sourceLocalInline: 'local analysis',
        sourceAiInline: 'AI analysis',
        deltaVsPrevious: 'vs. {{period}} ({{score}})',
        scoreComposition: 'Score breakdown',
        analysisLabel: 'Analysis',
        strengths: 'Strengths',
        improvements: 'To improve',
        viewHistory: 'View history',
        copySummary: 'Copy summary',
        summaryHeading: 'Financial Health — {{period}}',
        summaryScore: 'Score: {{score}}/100 ({{band}})',
        summaryStrengths: 'Strengths:',
        summaryImprovements: 'To improve:',
        band: {
            excellent: 'Excellent',
            good: 'Good',
            regular: 'Fair',
            fragile: 'Fragile',
        },
        delta: {
            zero: '0',
            none: '—',
            value: '{{sign}}{{value}}',
        },
        subScores: {
            savingsLabel: 'Savings',
            savingsNoteEmpty: 'No income recorded in the period.',
            savingsNote: 'Savings rate at {{rate}}% (benchmark: 20%).',
            emergencyLabel: 'Reserve',
            emergencyNoteEmpty: 'No reserve identified.',
            emergencyNote: 'Covers {{months}} months of expenses (target: 3 to 6).',
            creditLabel: 'Debt',
            creditNoteEmpty: 'No credit card usage.',
            creditNote: 'Card usage at {{usage}}% of the limit.',
            budgetLabel: 'Budget',
            budgetNoteEmpty: 'No active budgets in the period.',
            budgetNote: '{{pct}}% of budgets within the limit.',
            regularityLabel: 'Regularity',
            regularityNote: 'Transactions on {{pct}}% of the days in the month.',
            diversificationLabel: 'Income sources',
            diversificationNoteEmpty: 'No income source in the period.',
            diversificationNote: '{{sources}} income source(s) recorded.',
        },
    },
    errors: {
        copyableIntro: 'Paste this content into an external AI chat:',
        noApiKey: 'No API key has been configured. The app can generate the result locally, and you can copy the prompt to use in an external chat.',
        consentRequired: 'To use AI, you need to authorize sending the financial summary to the provider. In the meantime, the app generates the result locally.',
        invalidKeyGemini: 'The Gemini key looks invalid or lacks permission for this model. Google is retiring the older keys (the ones starting with "AIza") — if that is your case, generate a new one in Google AI Studio and paste it here.',
        invalidKeyGeminiMalformed: 'Google rejected this key. It was most likely pasted incomplete or picked up a stray space — clear the field, copy the whole key from Google AI Studio and paste it again. If it keeps failing, generate a new key.',
        invalidKeyOpenai: 'The OpenAI key looks invalid or lacks permission for this model.',
        invalidKeyOpenrouter: 'The OpenRouter key looks invalid. Check that it was pasted in full (it starts with "sk-or-") or generate a new one at openrouter.ai.',
        noCreditsOpenrouter: 'The OpenRouter key is valid, but the account is out of credits. Add credits at openrouter.ai or pick a free model (with ":free" in the name) in the model field.',
        rateLimitGemini: 'Gemini\'s temporary limit has been reached. This is common on the free tier. Wait a bit or copy the prompt to use outside the app.',
        rateLimitOpenai: 'OpenAI\'s temporary limit has been reached. Wait a bit or copy the prompt to use outside the app.',
        rateLimitOpenrouter: 'OpenRouter\'s temporary limit has been reached — common on free models. Wait a bit or switch models.',
        timeout: 'The AI request took too long and was interrupted. You can try again or copy the prompt.',
        modelUnavailable: 'The provider accepted the key but could not find the AI model for this project. If the key was just created, wait a few minutes and try again; if it persists, generate a new one in Google AI Studio.',
        modelUnavailableOpenrouter: 'OpenRouter could not find that model. Check the exact ID at openrouter.ai/models — or leave the model field empty to use the app default.',
        providerUnavailable: 'The provider server responded with an error. It is usually a momentary outage — try again in a few minutes.',
        networkUnreachable: 'Could not reach the provider server — the call never got there. A VPN, private DNS, ad blocker or corporate network usually blocks this address. Trying over mobile data helps confirm it.',
        connectionFailed: 'Couldn\'t validate the connection to the AI right now.',
        generic: 'Couldn\'t complete the AI analysis right now. You can try again or copy the prompt to use in an external chat.',
        connectionUnknown: 'Unknown error while validating the connection.',
    },
    local: {
        analysis: {
            intro: 'Your local financial health came in at {{score}}/100 for the period {{period}}.',
            savingsRate: 'The savings rate is at {{rate}}%.',
            creditUsage: 'Aggregate card usage is at {{usage}}% of the limit.',
            reserve: 'The reserve covers about {{months}} months of expenses.',
            adjustBudget: 'The main adjustment now is to get back on track with your active budgets.',
            adjustSavings: 'The main adjustment now is to increase your recurring monthly savings.',
            balanced: 'The picture is relatively balanced, with room to refine goals and consistency.',
        },
        strengths: {
            savings: 'Savings rate at {{rate}}%, above the 20% benchmark.',
            budget: 'You kept {{pct}}% of your active budgets within the limit.',
            credit: 'Card usage under control at {{usage}}% of the total limit.',
            reserve: 'Your reserve covers about {{months}} months of expenses.',
            topCategory: 'Your largest spending category accounts for {{pct}}% of the period\'s expenses.',
        },
        improvements: {
            savings: 'The savings rate is at {{rate}}%; try to bring it to at least 10%.',
            credit: 'Card usage is at {{usage}}% of the limit, above the comfortable zone.',
            reserve: 'The reserve covers {{months}} months; the minimum target is between 3 and 6.',
            overBudget: '{{category}} went over budget by {{amount}}.',
            budgetAdherence: 'Only {{pct}}% of your active budgets stayed within plan.',
        },
        tips: {
            savingsTitle: 'Boost your monthly savings',
            savingsDescription: 'Your savings rate is at {{rate}}%. Try to set aside at least 10% of your income as soon as the money comes in.',
            creditTitle: 'Ease the pressure on your card',
            creditDescription: 'Your card is using {{usage}}% of the total limit. Cutting back on credit purchases helps avoid another squeeze at the next closing date.',
            overBudgetTitle: 'Review {{category}}',
            overBudgetDescription: '{{category}} went over budget by {{amount}}. It\'s worth adjusting the limit or trimming this group back this month.',
            reserveTitle: 'Grow your emergency fund',
            reserveDescription: 'Right now your reserve covers {{months}} months of expenses. A healthy minimum is usually between 3 and 6 months.',
            subscriptionsTitle: 'Review recurring subscriptions',
            subscriptionsDescription: 'Subscriptions add up to {{pct}}% of your monthly income. There may be easy cuts here.',
            consistencyTitle: 'Keep it consistent',
            consistencyDescription: 'Your current picture is relatively balanced. Keep logging expenses regularly and review your goals once a month.',
        },
        narrative: {
            incomeExpense: 'In {{period}}, your income was {{income}} and your spending added up to {{expenses}}.',
            savingsAbove: 'You saved {{rate}}% of your income, above the 20% benchmark.',
            savingsBelow: 'The savings rate came in at {{rate}}%, below the recommended 20%.',
            savingsNegative: 'Spending exceeded income this period.',
            topExpense: 'The largest expense was {{category}} at {{amount}} ({{pct}}% of the total).',
            expensesTrend: 'Spending changed {{change}} compared to the previous month.',
            budgetAdherence: '{{pct}}% of budgets stayed within plan.',
        },
        highlights: {
            topIncrease: '{{category}} rose {{change}} vs. previous month.',
            topDecrease: '{{category}} dropped {{change}} vs. previous month.',
            savingsAbove: 'Savings rate at {{pct}}%, above target.',
            savingsLow: 'Savings rate of just {{pct}}%. Heads up!',
            reserveSolid: 'Solid emergency fund: {{months}} months.',
            reserveLow: 'Reserve covers just {{months}} months. Target: 3-6.',
            overBudget_one: '{{count}} budget over the limit this month.',
            overBudget_other: '{{count}} budgets over the limit this month.',
            creditAlert: 'Card usage at {{pct}}% of the limit. Warning zone.',
        },
        chat: {
            noExpenses: 'I couldn\'t find any expenses recorded in this period.',
            expenses: 'Total expenses: {{total}}. Top categories: {{categories}}.',
            noIncome: 'I couldn\'t find any income recorded in this period.',
            income: 'Total income: {{total}}. Sources: {{sources}}.',
            noCaixinhas: 'You don\'t have any pockets yet.',
            caixinhaLine: '{{name}}: {{current}}',
            caixinhaLineWithTarget: '{{name}}: {{current}} of {{target}}',
            caixinhas: 'You have {{reserved}} reserved in pockets: {{lines}}.',
            reservedParenthetical: ' ({{reserved}} reserved in pockets)',
            balance: 'You have {{available}} available to spend{{reservedPart}}. Total cash: {{cashTotal}}. {{days}} day(s) left until the end of the month.',
            noSavingsData: 'Not enough data to calculate the savings rate.',
            savings: 'Your savings rate is at {{rate}}%. {{note}}',
            savingsAbove: 'Above the 20% benchmark!',
            savingsBelow: 'The recommended benchmark is at least 20%.',
            noCreditData: 'I couldn\'t find any credit card data.',
            credit: 'Aggregate card usage is at {{usage}}% of the total limit. {{note}}',
            creditRisk: 'Heads up: above 70% is considered a risk zone.',
            creditComfortable: 'Within a comfortable range.',
            noBudgets: 'No active budget found.',
            budget: 'Budget adherence: {{pct}}%. {{note}}',
            budgetOver_one: '{{count}} category went over the limit.',
            budgetOver_other: '{{count}} categories went over the limit.',
            budgetAllWithin: 'All within plan.',
            noReserveData: 'No data to calculate the emergency fund.',
            reserve: 'Your reserve covers {{months}} months of expenses. {{note}}',
            reserveAbove: 'Above the recommended minimum of 3 months.',
            reserveIdeal: 'Ideally you\'d have between 3 and 6 months.',
            noGoals: 'No active goal found.',
            goalLine: '{{name}}: {{pct}}% ({{current}} of {{target}})',
            goals: 'Active goals: {{lines}}.',
            subscriptions: 'Total subscriptions/recurring is {{total}}/month{{incomePart}}.',
            subscriptionsIncomePart: ', accounting for {{pct}}% of income',
            noSubscriptions: 'I couldn\'t find any recurring expenses recorded.',
            noData: 'no data',
            fallback: 'Income: {{income}}. Expenses: {{expenses}}. Cash: {{cash}}. Savings: {{savings}}. Score: {{score}}/100. For more detailed questions, set up an API key in settings.',
        },
        insights: {
            topIncrease: '{{category}} rose {{change}} vs. previous month',
            topDecrease: '{{category}} dropped {{change}} vs. previous month',
            overBudget: '{{category}} went over budget by {{amount}}',
            savingsAbove: 'Savings at {{pct}}% — above target',
            expensesOverIncome: 'Spending exceeded income by {{amount}} this month',
            savingsLow: 'Savings at just {{pct}}% — target: 20%',
            creditAlert: 'Card using {{usage}}% of the limit',
            noActivity: 'No activity recorded this month',
        },
    },
} satisfies DeepPartialStrings<TranslationSchema['ai']>;
